/**
 * `TRACE_animation_binding` passthrough for gltf-transform.
 *
 * gltf-transform drops every extension it has no registered handler for, so
 * re-serializing an avatar or performance GLB through the optimizer would
 * silently strip mesh `targetNames` and node `sources`
 * (docs-internal/extensions/TRACE_animation_binding/README.md). This extension
 * carries the JSON object of each node, mesh and animation through unchanged.
 * It is written to `extensionsUsed` only, never `extensionsRequired`.
 * (Trace's driven-animation `extras.trace.driver` is extras, which
 * gltf-transform keeps on its own.)
 *
 * The payload is opaque here: nothing is re-indexed, so a transform that
 * reorders or merges the objects it refers to (join, flatten, prune) can leave
 * a `sources[].externalAsset` index stale. Names survive.
 */

import {
  type Animation,
  Extension,
  ExtensionProperty,
  type IProperty,
  type Mesh,
  type Node,
  type Nullable,
  PropertyType,
  type ReaderContext,
  type WriterContext,
} from '@gltf-transform/core';
import { TRACE_ANIMATION_BINDING } from '../../loaders/formats/gltf21/animationBinding/names';

type BindingJson = Record<string, unknown>;

interface IAnimationBindingData extends IProperty {
  data: BindingJson;
}

/** The raw `TRACE_animation_binding` object of one node, mesh or animation. */
export class AnimationBindingData extends ExtensionProperty<IAnimationBindingData> {
  static EXTENSION_NAME = TRACE_ANIMATION_BINDING;
  declare extensionName: typeof TRACE_ANIMATION_BINDING;
  declare propertyType: 'AnimationBindingData';
  declare parentTypes: [PropertyType.NODE, PropertyType.MESH, PropertyType.ANIMATION];

  protected init(): void {
    this.extensionName = TRACE_ANIMATION_BINDING;
    this.propertyType = 'AnimationBindingData';
    this.parentTypes = [PropertyType.NODE, PropertyType.MESH, PropertyType.ANIMATION];
  }

  protected getDefaults(): Nullable<IAnimationBindingData> {
    return Object.assign(super.getDefaults(), { data: {} });
  }

  getData(): BindingJson {
    return this.get('data');
  }

  setData(data: BindingJson): this {
    return this.set('data', data);
  }
}

type Carrier = Node | Mesh | Animation;
type JsonDef = { extensions?: Record<string, unknown> };

export class TraceAnimationBinding extends Extension {
  static readonly EXTENSION_NAME = TRACE_ANIMATION_BINDING;
  readonly extensionName = TRACE_ANIMATION_BINDING;

  read(context: ReaderContext): this {
    const json = context.jsonDoc.json;
    const collections: Array<[JsonDef[] | undefined, Carrier[]]> = [
      [json.nodes, context.nodes],
      [json.meshes, context.meshes],
      [json.animations, context.animations],
    ];
    for (const [defs, properties] of collections) {
      (defs ?? []).forEach((def, index) => {
        const ext = def.extensions?.[TRACE_ANIMATION_BINDING];
        if (!ext || typeof ext !== 'object') return;
        const data = new AnimationBindingData(this.document.getGraph())
          .setData(structuredClone(ext) as BindingJson);
        properties[index]?.setExtension(TRACE_ANIMATION_BINDING, data);
      });
    }
    return this;
  }

  write(context: WriterContext): this {
    const json = context.jsonDoc.json;
    const collections: Array<[Map<Carrier, number>, JsonDef[] | undefined]> = [
      [context.nodeIndexMap, json.nodes],
      [context.meshIndexMap, json.meshes],
      [context.animationIndexMap, json.animations],
    ];
    for (const [indexMap, defs] of collections) {
      for (const [property, index] of indexMap) {
        const data = property.getExtension<AnimationBindingData>(TRACE_ANIMATION_BINDING);
        const def = defs?.[index];
        if (!data || !def) continue;
        def.extensions = { ...(def.extensions ?? {}), [TRACE_ANIMATION_BINDING]: structuredClone(data.getData()) };
      }
    }
    return this;
  }
}
